import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useTitle } from "@/hooks/use-title";
import { useTranslation } from "@/i18n/LanguageContext";
import Header from "@/components/header";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";
import { useState, useEffect } from "react";

interface CustomPuzzle {
  id: number;
  title: string;
  description: string;
  address: string;
  prize: string;
  userId: number;
  solved: boolean;
  imageUrl?: string;
  user: {
    id: number;
    displayName?: string;
    email: string;
  };
}

interface User {
  id: number;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  cacatais: number;
}

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  imageUrl: z.string().optional(),
  prizeIncrease: z.number().min(0, "Prize increase must be 0 or greater").default(0),
});

type FormData = z.infer<typeof formSchema>;

export default function EditPuzzlePage() {
  const params = useParams();
  const puzzleId = params.id;
  const { t, formatNumber } = useTranslation();
  const { toast } = useToast();
  const [_, setLocation] = useLocation();
  const queryClient = useQueryClient();
  useTitle(t("other.edit_puzzle.title"));
  const [feeAmount, setFeeAmount] = useState(0);
  const [totalCost, setTotalCost] = useState(0);

  // Fetch the puzzle data
  const { data: puzzle, isLoading: isPuzzleLoading, error: puzzleError } = useQuery<CustomPuzzle>({
    queryKey: [`/api/puzzles/custom/${puzzleId}`],
    enabled: !!puzzleId,
  });

  // Fetch the current user
  const { data: user, isLoading: isUserLoading } = useQuery<User>({
    queryKey: ["/api/auth/user"],
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      imageUrl: "",
      prizeIncrease: 0,
    },
  });

  // Watch the prizeIncrease value to calculate fee
  const prizeIncreaseValue = form.watch("prizeIncrease");
  
  // Calculate fee and total cost when prizeIncrease changes
  useEffect(() => {
    const fee = prizeIncreaseValue * 0.1; // 10% fee
    setFeeAmount(fee);
    setTotalCost(prizeIncreaseValue + fee);
  }, [prizeIncreaseValue]);

  // Set form default values when puzzle data is loaded
  useEffect(() => {
    if (puzzle) {
      form.reset({
        title: puzzle.title,
        description: puzzle.description,
        imageUrl: puzzle.imageUrl || "",
      });
    }
  }, [puzzle, form]);

  const updatePuzzle = useMutation({
    mutationFn: async (data: FormData) => {
      // Include fee information in the request
      const requestData = {
        ...data,
        fee: feeAmount,
        totalCost: totalCost
      };
      
      const res = await fetch(`/api/puzzles/custom/${puzzleId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to update puzzle");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/custom/${puzzleId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/puzzles/custom"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] }); // Refresh user data to update balance
      toast({ title: t("other.edit_puzzle.success") });
      setLocation("/desafios-cacatais");
    },
    onError: (error) => {
      toast({
        title: t("other.edit_puzzle.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isLoading = isPuzzleLoading || isUserLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (puzzleError) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("other.edit_puzzle.error_title")}</CardTitle>
              <CardDescription>{t("other.edit_puzzle.error_description")}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("auth.required")}</CardTitle>
              <CardDescription>{t("auth.sign_in_required")}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  // Check if the user is the owner of the puzzle
  if (puzzle && user && puzzle.userId !== user.id && user.email !== 'leo.majowka@gmail.com') {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("other.edit_puzzle.unauthorized_title")}</CardTitle>
              <CardDescription>{t("other.edit_puzzle.unauthorized_description")}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  // Check if the puzzle is already solved
  if (puzzle && puzzle.solved) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("other.edit_puzzle.solved_title")}</CardTitle>
              <CardDescription>{t("other.edit_puzzle.solved_description")}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  const onSubmit = (data: FormData) => {
    // Check if user has enough balance for the prize increase
    if (user && data.prizeIncrease > 0 && totalCost > user.cacatais) {
      toast({
        title: t("puzzles.insufficientBalance"),
        description: t("create_puzzle.form.total_cost", { amount: formatNumber(totalCost, 6) }) + 
                    ` (${t("create_puzzle.form.prize_label", { amount: formatNumber(data.prizeIncrease) })} + ${t("create_puzzle.form.fee_label", { amount: formatNumber(feeAmount, 6) })})`,
        variant: "destructive",
      });
      return;
    }
    
    updatePuzzle.mutate(data);
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="container mx-auto pt-24 px-6 pb-12">
        <Card className="bg-zinc-900 border-zinc-800 max-w-3xl mx-auto shadow-lg">
          <CardHeader className="border-b border-zinc-800 pb-6">
            <CardTitle className="text-2xl text-white">{t("other.edit_puzzle.title")}</CardTitle>
            <CardDescription className="text-zinc-400">{t("other.edit_puzzle.description")}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("other.edit_puzzle.form.title")}</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-zinc-800 border-zinc-700" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("other.edit_puzzle.form.description")}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          className="bg-zinc-800 border-zinc-700 min-h-[150px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="imageUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("other.edit_puzzle.form.image")}</FormLabel>
                      <FormControl>
                        <div className="space-y-4">
                          {field.value && (
                            <div className="relative w-full h-48 overflow-hidden rounded-md">
                              <img 
                                src={field.value} 
                                alt={puzzle?.title || "Puzzle image"} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <ImageUpload
                            onImageUpload={(url) => field.onChange(url)}
                            className="bg-zinc-800 border-zinc-700"
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        {t("other.edit_puzzle.form.image_description")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="prizeIncrease"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("other.edit_puzzle.form.prize_increase") || "Increase Prize Amount"}</FormLabel>
                      <FormControl>
                        <Input 
                          type="number"
                          step="0.000001"
                          min="0"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      </FormControl>
                      <FormDescription>
                        {t("other.edit_puzzle.form.prize_increase_description") || "Increase the prize amount for this puzzle. Current prize: "} 
                        {' '}{formatNumber(parseFloat(puzzle?.prize || "0"), 6)}
                        <div className="mt-2 text-amber-500">
                          {field.value > 0 && (
                            <>
                              <p>{t("other.create_puzzle.form.fee_info") || "A 10% fee will be charged on the prize increase."}</p>
                              <p>{t("other.edit_puzzle.form.increase_prize_label", { amount: formatNumber(field.value) }) || `Prize Increase: ${formatNumber(field.value)}`}</p>
                              <p>{t("other.create_puzzle.form.fee_label", { amount: formatNumber(feeAmount, 2) }) || `Fee (10%): ${formatNumber(feeAmount, 2)}`}</p>
                              <p>{t("other.create_puzzle.form.total_cost", { amount: formatNumber(totalCost, 2) }) || `Total Cost: ${formatNumber(totalCost, 2)}`}</p>
                              {user && <p>{t("other.create_puzzle.form.your_balance", { amount: formatNumber(user.cacatais || 0, 2) }) || `Your Balance: ${formatNumber(user.cacatais || 0, 2)}`}</p>}
                              {user && totalCost > (user.cacatais || 0) && (
                                <p className="text-red-500 font-bold">{t("other.create_puzzle.form.insufficient_balance") || "Insufficient balance"}</p>
                              )}
                            </>
                          )}
                        </div>
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLocation("/desafios-cacatais")}
                  >
                    {t("other.edit_puzzle.form.cancel")}
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={updatePuzzle.isPending || (user && prizeIncreaseValue > 0 && totalCost > (user.cacatais || 0))}
                  >
                    {updatePuzzle.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("other.edit_puzzle.form.saving")}
                      </>
                    ) : (
                      t("other.edit_puzzle.form.save")
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
